import { FC, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import InfoCircleFilled from 'dibs-icons/exports/legacy/InfoCircleFilled';

import { info, estimateInfo } from '../messages/pdpPricingGuidanceMessages';
import { trackPricingGuidanceModuleTooltipView } from '../PdpSharedPricingGuidance/pricingGuidanceTracking';
import { useShowEstimatedValueFurnitureTestVariant } from '../helpers/estimatedValueFurnitureTestHelpers';

import { PdpRespPricingGuidanceInfo_item$key } from './__generated__/PdpRespPricingGuidanceInfo_item.graphql';

type Props = {
    item: PdpRespPricingGuidanceInfo_item$key;
    iconClassName?: string;
    trigger?: 'siderail';
};

export const PdpRespPricingGuidanceInfo: FC<Props> = ({
    item: itemRef,
    iconClassName,
    trigger,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpRespPricingGuidanceInfo_item on Item {
                ...estimatedValueFurnitureTestHelpers_item
                ...pricingGuidanceTracking_item
            }
        `,
        itemRef
    );

    const [showTooltip, setShowTooltip] = useState(false);

    const showEstimatedValueFurnitureTestVariantCopy = useShowEstimatedValueFurnitureTestVariant({
        item,
    });

    const onShowTooltip = (): void => {
        if (!showTooltip) {
            setShowTooltip(true);
            trackPricingGuidanceModuleTooltipView({
                item,
                trigger,
                isEstimatedValueFurnitureTestVariant: showEstimatedValueFurnitureTestVariantCopy,
            });
        }
    };

    const Icon = showTooltip ? InfoCircleFilled : InfoCircle;

    return (
        <div
            onMouseEnter={onShowTooltip}
            onMouseLeave={() => setShowTooltip(false)}
            className={classnames(dibsCss.relative, dibsCss.flex, dibsCss.itemsCenter)}
        >
            <div className={classnames(dibsCss.cursorPointer, dibsCss.flex, dibsCss.itemsCenter)}>
                <Icon className={classnames(dibsCss.h14Px, dibsCss.w14Px, iconClassName)} />
            </div>
            <Tooltip
                isVisible={showTooltip}
                direction={trigger === 'siderail' ? 'top' : 'right'}
                zIndex="low"
            >
                <div className={dibsCss.whitespaceNormal} id="pdp.pdpRespPricingGuidance.tooltip">
                    {showEstimatedValueFurnitureTestVariantCopy ? estimateInfo : info}
                </div>
            </Tooltip>
        </div>
    );
};
